export const ACTIVITY_SESSION_STORAGE_KEY = 'activity-session';
export const ACTIVITY_HEARTBEAT_MS = 5 * 60 * 1000;
export const ACTIVITY_HIDDEN_GRACE_MS = 5 * 60 * 1000;

export interface StoredActivitySession {
  uuid: string;
  organisationId: string;
  startedAt: string;
  hiddenAt: string | null;
}

export interface ActivitySessionReport {
  session_uuid: string;
  platform: 'web';
  started_at: string;
  last_seen_at: string;
  ended_at?: string;
}

export type BootDecision =
  | { type: 'start' }
  | { type: 'resume'; session: StoredActivitySession }
  | { type: 'end-then-start'; previous: StoredActivitySession; endedAt: string };

export function clampTimestamp(candidateIso: string, startedAt: string): string {
  const candidateMs = Date.parse(candidateIso);
  const startedMs = Date.parse(startedAt);
  if (Number.isNaN(candidateMs) || Number.isNaN(startedMs)) return candidateIso;
  return candidateMs < startedMs ? startedAt : candidateIso;
}

export function isHiddenWithinGrace(hiddenAt: string | null, nowMs: number): boolean {
  if (!hiddenAt) return true;

  const hiddenMs = Date.parse(hiddenAt);
  if (Number.isNaN(hiddenMs)) return false;

  return nowMs - hiddenMs < ACTIVITY_HIDDEN_GRACE_MS;
}

export function decideBoot(args: {
  stored: StoredActivitySession | null;
  organisationId: string;
  nowIso: string;
  nowMs: number;
  // False for a new document that merely inherited sessionStorage (a duplicated tab).
  // The copied uuid still belongs to the original tab, so it must not be resumed or ended.
  adoptStoredSession: boolean;
}): BootDecision {
  const {
    stored, organisationId, nowIso, nowMs, adoptStoredSession,
  } = args;
  if (!stored || !adoptStoredSession) return { type: 'start' };

  const sameOrganisation = stored.organisationId === organisationId;
  if (sameOrganisation && isHiddenWithinGrace(stored.hiddenAt, nowMs)) {
    return { type: 'resume', session: stored };
  }

  const { hiddenAt } = stored;
  const hiddenMs = hiddenAt ? Date.parse(hiddenAt) : Number.NaN;
  const endedAt = sameOrganisation && hiddenAt && !Number.isNaN(hiddenMs)
    ? clampTimestamp(hiddenAt, stored.startedAt)
    : clampTimestamp(nowIso, stored.startedAt);

  return { type: 'end-then-start', previous: stored, endedAt };
}

export function buildActivitySessionPayload(
  session: StoredActivitySession,
  lastSeenAt: string,
  endedAt?: string,
): ActivitySessionReport {
  const payload: ActivitySessionReport = {
    session_uuid: session.uuid,
    platform: 'web',
    started_at: session.startedAt,
    last_seen_at: clampTimestamp(lastSeenAt, session.startedAt),
  };

  if (!endedAt) return payload;

  payload.ended_at = clampTimestamp(endedAt, session.startedAt);
  const lastSeenMs = Date.parse(payload.last_seen_at);
  const endedMs = Date.parse(payload.ended_at);
  if (!Number.isNaN(lastSeenMs) && !Number.isNaN(endedMs) && lastSeenMs > endedMs) {
    payload.last_seen_at = payload.ended_at;
  }

  return payload;
}

export function isStoredActivitySession(value: unknown): value is StoredActivitySession {
  if (!value || typeof value !== 'object') return false;

  const session = value as StoredActivitySession;
  return typeof session.uuid === 'string'
    && session.uuid.length > 0
    && typeof session.organisationId === 'string'
    && session.organisationId.length > 0
    && typeof session.startedAt === 'string'
    && (session.hiddenAt === null || typeof session.hiddenAt === 'string');
}
